import { HttpMethods } from 'constants/http';
import fetch from 'isomorphic-unfetch';

interface ConstructorConfig {
  baseUrl?: string;
  headers?: Record<string, string>;
  timeoutMs?: number;
}

export interface RequestConfig extends RequestInit {
  payload?: object;
  timeoutMs?: number;
}

class APIService {
  baseUrl: string;
  headers: Record<string, string>;
  timeoutMs?: number;

  constructor({ baseUrl = '', headers = {}, timeoutMs }: ConstructorConfig) {
    this.baseUrl = baseUrl;
    this.headers = headers;
    this.timeoutMs = timeoutMs;
  }

  request(
    endpoint: string,
    { payload, timeoutMs = this.timeoutMs, ...customConfig }: RequestConfig = {},
  ) {
    const headers = { 'content-type': 'application/json', ...this.headers };
    const externalSignal = customConfig.signal;
    const abortController = timeoutMs ? new AbortController() : null;
    const forwardAbort = () => abortController?.abort();
    if (abortController && externalSignal) {
      if (externalSignal.aborted) {
        abortController.abort();
      } else {
        externalSignal.addEventListener('abort', forwardAbort, { once: true });
      }
    }
    const timeout = abortController
      ? setTimeout(() => abortController.abort(), timeoutMs)
      : undefined;
    const config: RequestInit = {
      method: payload ? HttpMethods.Post : HttpMethods.Get,
      ...customConfig,
      signal: abortController?.signal || externalSignal,
      headers: {
        ...headers,
        ...customConfig.headers,
      },
    };

    if (payload) {
      config.body = JSON.stringify(payload);
    }

    return fetch(`${this.baseUrl}/${endpoint}`, config)
      .then(async (response) => {
        const data = await response.json();
        if (response.ok) {
          return data;
        } else {
          // TODO: Log in sentry
          return Promise.reject(data);
        }
      })
      .finally(() => {
        if (timeout) clearTimeout(timeout);
        externalSignal?.removeEventListener('abort', forwardAbort);
      });
  }

  get(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Get;
    return this.request(endpoint, config);
  }

  post(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Post;
    return this.request(endpoint, config);
  }

  put(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Put;
    return this.request(endpoint, config);
  }

  patch(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Patch;
    return this.request(endpoint, config);
  }

  delete(endpoint: string, config: RequestConfig = {}) {
    config.method = HttpMethods.Delete;
    return this.request(endpoint, config);
  }
}

export default APIService;
